/**
 * Handles "intents" sent back from the server as a result of
 * form submissions or navigations: replace content or display errors
 * @module embed/intents
 */

import { extractErrors, renderErrors } from '@/lib/simpleFormErrors';
import { whileMaintainingScroll } from '@/lib/scroll';
import { transitionSlide } from './transition_slide';

const SELECTOR_SLIDE = '.js-bcs-slide-content';
const ATTR_URL = 'data-slide-url';

const INTENTS = {
  replaceSlide({ html }) {
    return function(slide, { setup, slideIndex, pushState = true } = {}) {
      const dom = new DOMParser().parseFromString(html, 'text/html');
      const newSlide = dom.querySelector(SELECTOR_SLIDE);
      // Fade out old node, fade in new node
      return transitionSlide(newSlide, slide, {
        beforeEnter: setup
      })
        .then(() => {
          whileMaintainingScroll(() => {
            newSlide.setAttribute('tabindex', '-1');
            newSlide.focus();
          });
          return;
        })
        .then(() => {
          if (pushState) {
            // Ensure there's a slide index on the first entry
            // and that the URL is the backend one
            // For when navigating back
            if (!window.history.state) {
              window.history.replaceState(
                {
                  // TODO: Remove slideIndex from here and use a `let` variable
                  // in embed.js
                  // TODO: Move handling of URL out of intent handling and into embed.js
                  // so all URL handling is in a common place
                  slideIndex,
                  slideUrl: slide.getAttribute(ATTR_URL),
                  initialSlide: true
                },
                document.title,
                window.location
              );
            }

            const slideUrl = newSlide.getAttribute(ATTR_URL);
            window.history.pushState(
              {
                slideIndex,
                slideUrl
              },
              document.title,
              replaceOrigin(slideUrl)
            );
          }
          return newSlide;
        })
        .then(() => {
          const event = new CustomEvent('didReplaceSlide', { bubbles: true });
          newSlide.dispatchEvent(event);
          return newSlide;
        });
    };
  },
  displayErrors({ html }) {
    return function(form) {
      const dom = new DOMParser().parseFromString(html, 'text/html');
      const errors = extractErrors(dom);
      renderErrors(form, errors, { focusFirstError: true });
    };
  }
};
/**
 * @param {HTMLElement} form The current form
 * @param {Array<Object>} intents The list of intents
 * @param {Object} context
 * @param {Function} context.setup The callback to setup JS behaviour on the new form
 * @param {int} context.slideIndex An identifier of which area has triggered the action,
 *                               so the URL history is tied to it and updates the page accordingly
 *                               when navigating with the back button
 * @param {Boolean} [context.pushState=true] A flag to allow/prevent pushState
 */
export function handleIntents(form, intents = [], context) {
  // TODO: Be a bit defensive in case intent does not exist
  return Promise.all(
    intents.map(({ intent, data }) => INTENTS[intent](data)(form, context))
  );
}

function replaceOrigin(url) {
  const urlObject = new URL(url);
  urlObject.protocol = window.location.protocol;
  urlObject.host = window.location.host;
  return urlObject.href;
}



// WEBPACK FOOTER //
// ./app/webpack/javascripts/embed/intents.js